/**
 * Terrain : tuiles de 256 m (8 x 8 sur Val Cendrée, 16 x 16 sur une carte de
 * 4 km) construites sur le champ de hauteur
 * partagé, chacune en trois niveaux de détail (pas de 4, 16 et 64 m).
 *
 * - LOD par distance caméra, jupes verticales pour masquer les fissures
 *   entre tuiles de niveaux différents ;
 * - frustum culling par tuile (sphère englobante) ;
 * - occlusion basique : une tuile entièrement cachée derrière le relief
 *   (rayons caméra -> coins testés sur le champ de hauteur) n'est pas
 *   dessinée, et son décor non plus ;
 * - couleurs par sommet pour le fond (neige, roche selon la pente, village) ;
 * - pistes dessinées dans le shader à partir de la texture raster (4 m) :
 *   neige damée, bourrelets, glace, manque de neige, et surcouche « plan
 *   des pistes » commandée par un uniform. Le tracé reste net à tout LOD.
 */
import * as THREE from 'three/webgpu';
import { float, mix, positionWorld, smoothstep, step, texture, uniform, vec2, vec3, vertexColor } from 'three/tsl';
import { createNoise2D, fbm, type Heightfield } from '@snowchain/shared';
import type { PisteRaster } from './piste-raster';
import type { Frustum } from './instancing';
import { groomSurface } from './groom-surface';

const CHUNK = 64;
const STEPS = [1, 4, 16];
const SKIRT = 18;

/** Tuiles par côté de la carte (64 échantillons, 256 m chacune). */
export function chunksPerSide(hf: Heightfield): number {
  return (hf.res - 1) / CHUNK;
}

export interface TerrainChunk {
  i: number;
  j: number;
  cx: number;
  cz: number;
  minH: number;
  maxH: number;
  radius: number;
  meshes: (THREE.Mesh | null)[];
  lod: number;
  visible: boolean;
  occluded: boolean;
  dirty: boolean;
}

/** Surcouche du plan des pistes (0 = masquée, 1 = affichée). */
export const pisteOverlay = uniform(0);

const SNOW = new THREE.Color(0xe4ebf1);
const SNOW_SHADE = new THREE.Color(0xcfdbe5);
const ROCK = new THREE.Color(0x6e7c86);
const ROCK_DARK = new THREE.Color(0x465864);
const EARTH = new THREE.Color(0x7d6b52);
const VILLAGE = new THREE.Color(0xeee8de);

function detailTexture(): THREE.DataTexture {
  // Bruit raccordable : mélange bilinéaire de quatre échantillons décalés
  // d'une période W, identique sur les bords opposés de la texture.
  const size = 256;
  const data = new Uint8Array(size * size * 4);
  const n = createNoise2D(77);
  const W = 8;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      const fx = u * W;
      const fy = v * W;
      const val =
        fbm(n, fx, fy, 4) * (1 - u) * (1 - v) + fbm(n, fx - W, fy, 4) * u * (1 - v) + fbm(n, fx, fy - W, 4) * (1 - u) * v + fbm(n, fx - W, fy - W, 4) * u * v;
      const k = (y * size + x) * 4;
      const b = Math.round(Math.min(1, Math.max(0, 0.5 + 0.5 * val)) * 255);
      data[k] = b;
      data[k + 1] = b;
      data[k + 2] = b;
      data[k + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

export class Terrain {
  readonly group = new THREE.Group();
  readonly chunks: TerrainChunk[] = [];
  /** Tuiles par côté. */
  readonly side: number;
  readonly material: THREE.MeshStandardNodeMaterial;
  private noise = createNoise2D(4242);
  /** Hauteur de neige naturelle hors piste (cm), pilotée par l'état des pistes. */
  naturalSnow = 70;
  trianglesDrawn = 0;
  chunksDrawn = 0;

  constructor(
    private readonly hf: Heightfield,
    raster: PisteRaster,
    private readonly village: { x: number; z: number; radius: number },
  ) {
    this.group.name = 'terrain';
    this.side = chunksPerSide(hf);
    this.material = this.createMaterial(raster);
    const size = CHUNK * hf.cell;
    for (let j = 0; j < this.side; j++) {
      for (let i = 0; i < this.side; i++) {
        let minH = Infinity;
        let maxH = -Infinity;
        for (let y = j * CHUNK; y <= (j + 1) * CHUNK; y++) {
          for (let x = i * CHUNK; x <= (i + 1) * CHUNK; x++) {
            const h = hf.at(x, y);
            if (h < minH) minH = h;
            if (h > maxH) maxH = h;
          }
        }
        this.chunks.push({
          i,
          j,
          cx: -hf.half + (i + 0.5) * size,
          cz: -hf.half + (j + 0.5) * size,
          minH,
          maxH,
          radius: Math.hypot(size / 2, size / 2, (maxH - minH) / 2),
          meshes: [null, null, null],
          lod: -1,
          visible: false,
          occluded: false,
          dirty: false,
        });
      }
    }
  }

  private createMaterial(raster: PisteRaster): THREE.MeshStandardNodeMaterial {
    const m = new THREE.MeshStandardNodeMaterial();
    m.vertexColors = true;
    m.roughness = 0.9;
    m.metalness = 0;
    const tex = detailTexture();
    const d1 = texture(tex, positionWorld.xz.mul(1 / 31)).r;
    const d2 = texture(tex, positionWorld.xz.mul(1 / 7.3)).r;
    const detail = float(0.93).add(d1.mul(0.07)).add(d2.mul(0.035));
    // Lecture du raster des pistes (texel centré sur l'échantillon du champ de hauteur).
    const { half, cell, res } = this.hf;
    const ruv = vec2(
      positionWorld.x.add(half).div(cell).add(0.5).div(res),
      positionWorld.z.add(half).div(cell).add(0.5).div(res),
    );
    const pt = texture(raster.texture, ruv);
    const cov = pt.r;
    const diff = pt.g.mul(3);
    const thin = pt.b;
    const quality = pt.a;
    // Neige damée : blanche et lisse ; bleutée si glacée ; grise puis terre si elle manque.
    const groomedWhite = vec3(1, 0.996, 0.985);
    const ice = vec3(0.78, 0.86, 0.93);
    const grey = vec3(0.7, 0.74, 0.76);
    const earth = vec3(0.42, 0.34, 0.25);
    let groomed = mix(groomedWhite, ice, smoothstep(0.55, 0.15, quality).mul(0.7));
    groomed = mix(groomed, grey, thin.mul(0.75));
    groomed = mix(groomed, earth, step(0.62, d2.add(thin.mul(0.6))).mul(thin.greaterThan(0.6).select(float(0.8), float(0))));
    // Velours de damage, traces, bosses et damage en cours de nuit (voir groom-surface.ts).
    groomed = groomSurface.shade(groomed, d2);
    // Bourrelet de neige plus sombre en bordure de piste.
    const edge = smoothstep(0.08, 0.4, cov).mul(float(1).sub(smoothstep(0.4, 0.85, cov)));
    groomed = mix(groomed, vec3(0.8, 0.85, 0.89), edge.mul(0.55));
    const pisteMask = smoothstep(0.06, 0.5, cov);
    const base = mix(vertexColor().rgb.mul(detail), groomed.mul(float(0.97).add(d2.mul(0.03))), pisteMask);
    // Surcouche du plan des pistes.
    const green = vec3(0.04, 0.33, 0.1);
    const blue = vec3(0.015, 0.12, 0.62);
    const red = vec3(0.72, 0.09, 0.02);
    const black = vec3(0.01, 0.012, 0.016);
    const overlayColor = mix(mix(green, blue, step(0.5, diff)), mix(red, black, step(2.5, diff)), step(1.5, diff));
    m.colorNode = mix(base, overlayColor, pisteMask.mul(pisteOverlay).mul(0.75));
    m.roughnessNode = mix(float(0.92), float(0.7), pisteMask);
    return m;
  }

  /* ---------------------------------------------------------------- */
  /* Couleurs de fond (par sommet)                                      */
  /* ---------------------------------------------------------------- */

  private shade(x: number, z: number, h: number, out: THREE.Color): number {
    const hf = this.hf;
    const slope = hf.slope(x, z);
    const n = this.noise(x / 90, z / 90);
    const n2 = this.noise(x / 9 + 13, z / 9 - 7);
    // Courbure : les creux sont un peu plus sombres (occlusion ambiante cuite).
    const lap = (hf.height(x + 8, z) + hf.height(x - 8, z) + hf.height(x, z + 8) + hf.height(x, z - 8)) / 4 - h;
    const ao = 1 - Math.min(0.14, Math.max(0, lap * 0.04));
    out.copy(SNOW).lerp(SNOW_SHADE, Math.min(1, Math.max(0, 0.35 + n * 0.45)));
    // Roche sur les fortes pentes, plus présente quand la neige manque.
    const thin = Math.max(0, (60 - this.naturalSnow) / 60);
    const rockStart = 0.72 - thin * 0.25;
    const rock = Math.min(1, Math.max(0, (slope - rockStart + n * 0.12) / 0.3));
    if (rock > 0) out.lerp(n2 > 0 ? ROCK : ROCK_DARK, rock * (0.65 + 0.35 * Math.abs(n2)));
    // Terre visible en bas de vallée si l'enneigement naturel est faible.
    if (thin > 0.2 && h < 60) out.lerp(EARTH, Math.min(1, (thin - 0.2) * (n2 > 0.15 ? 1.4 : 0.3)));
    // Village : neige tassée, teinte chaude discrète.
    const dv = Math.hypot(x - this.village.x, z - this.village.z);
    if (dv < this.village.radius * 1.2) out.lerp(VILLAGE, Math.min(1, (this.village.radius * 1.2 - dv) / 60) * 0.4);
    return ao;
  }

  /* ---------------------------------------------------------------- */
  /* Géométrie                                                          */
  /* ---------------------------------------------------------------- */

  private buildGeometry(c: TerrainChunk, lod: number): THREE.BufferGeometry {
    const stepCells = STEPS[lod] as number;
    const n = CHUNK / stepCells + 1;
    const total = n * n + 4 * n;
    const pos = new Float32Array(total * 3);
    const nor = new Float32Array(total * 3);
    const col = new Float32Array(total * 3);
    const tmp = new THREE.Color();
    const hf = this.hf;
    const set = (k: number, gi: number, gj: number, drop: number) => {
      const x = -hf.half + gi * hf.cell;
      const z = -hf.half + gj * hf.cell;
      const h = hf.at(gi, gj);
      pos[k * 3] = x;
      pos[k * 3 + 1] = h - drop;
      pos[k * 3 + 2] = z;
      const nn = hf.normal(x, z);
      nor[k * 3] = nn[0];
      nor[k * 3 + 1] = nn[1];
      nor[k * 3 + 2] = nn[2];
      const ao = this.shade(x, z, h, tmp);
      col[k * 3] = tmp.r * ao;
      col[k * 3 + 1] = tmp.g * ao;
      col[k * 3 + 2] = tmp.b * ao;
    };
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) set(y * n + x, c.i * CHUNK + x * stepCells, c.j * CHUNK + y * stepCells, 0);
    // Jupes : bords nord, sud, ouest, est.
    const base = n * n;
    for (let x = 0; x < n; x++) {
      set(base + x, c.i * CHUNK + x * stepCells, c.j * CHUNK, SKIRT);
      set(base + n + x, c.i * CHUNK + x * stepCells, c.j * CHUNK + CHUNK, SKIRT);
      set(base + 2 * n + x, c.i * CHUNK, c.j * CHUNK + x * stepCells, SKIRT);
      set(base + 3 * n + x, c.i * CHUNK + CHUNK, c.j * CHUNK + x * stepCells, SKIRT);
    }
    const idx: number[] = [];
    for (let y = 0; y < n - 1; y++) {
      for (let x = 0; x < n - 1; x++) {
        const a = y * n + x;
        const b = a + 1;
        const d = a + n;
        const e = d + 1;
        idx.push(a, d, b, b, d, e);
      }
    }
    for (let k = 0; k < n - 1; k++) {
      const tn = k;
      const ts = (n - 1) * n + k;
      const tw = k * n;
      const te = k * n + (n - 1);
      idx.push(tn, tn + 1, base + k, tn + 1, base + k + 1, base + k);
      idx.push(ts, base + n + k, ts + 1, ts + 1, base + n + k, base + n + k + 1);
      idx.push(tw, base + 2 * n + k, tw + n, tw + n, base + 2 * n + k, base + 2 * n + k + 1);
      idx.push(te, te + n, base + 3 * n + k, te + n, base + 3 * n + k + 1, base + 3 * n + k);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setIndex(idx);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(c.cx, (c.minH + c.maxH) / 2, c.cz), c.radius);
    return g;
  }

  private mesh(c: TerrainChunk, lod: number): THREE.Mesh {
    let m = c.meshes[lod];
    if (!m) {
      m = new THREE.Mesh(this.buildGeometry(c, lod), this.material);
      m.name = `terrain_${c.i}_${c.j}_L${lod}`;
      m.receiveShadow = true;
      m.frustumCulled = false;
      m.matrixAutoUpdate = false;
      m.visible = false;
      this.group.add(m);
      c.meshes[lod] = m;
    }
    return m;
  }

  /** Refait les tuiles marquées (enneigement naturel, terrassements) ; budget par image. */
  refreshDirty(maxPerFrame = 2): void {
    let done = 0;
    for (const c of this.chunks) {
      if (!c.dirty) continue;
      for (let l = 0; l < c.meshes.length; l++) {
        const m = c.meshes[l];
        if (!m) continue;
        const old = m.geometry;
        m.geometry = this.buildGeometry(c, l);
        old.dispose();
      }
      c.dirty = false;
      if (++done >= maxPerFrame) break;
    }
  }

  markAllDirty(): void {
    for (const c of this.chunks) c.dirty = true;
  }

  /**
   * Relief modifié dans un rectangle (terrassements) : bornes des tuiles
   * touchées recalculées, géométrie refaite. Une cellule de marge couvre les
   * normales, calculées par différences centrées.
   */
  markDirtyRect(r: { x0: number; z0: number; x1: number; z1: number }): void {
    const cell = this.hf.cell;
    const size = CHUNK * cell;
    for (const c of this.chunks) {
      if (c.cx + size / 2 < r.x0 - cell || c.cx - size / 2 > r.x1 + cell) continue;
      if (c.cz + size / 2 < r.z0 - cell || c.cz - size / 2 > r.z1 + cell) continue;
      let minH = Infinity;
      let maxH = -Infinity;
      for (let y = c.j * CHUNK; y <= (c.j + 1) * CHUNK; y++) {
        for (let x = c.i * CHUNK; x <= (c.i + 1) * CHUNK; x++) {
          const h = this.hf.at(x, y);
          if (h < minH) minH = h;
          if (h > maxH) maxH = h;
        }
      }
      c.minH = minH;
      c.maxH = maxH;
      c.radius = Math.hypot(size / 2, size / 2, (maxH - minH) / 2);
      c.dirty = true;
    }
  }

  /** Choix du LOD et visibilité de chaque tuile. Renvoie vrai si la visibilité d'une tuile a changé. */
  update(camera: THREE.Camera, frustum: Frustum, occlusion: boolean): boolean {
    const cp = camera.position;
    let tris = 0;
    let drawn = 0;
    let changed = false;
    for (const c of this.chunks) {
      const inFrustum = frustum.sphere(c.cx, (c.minH + c.maxH) / 2, c.cz, c.radius);
      const visible = inFrustum && !(occlusion && c.occluded);
      if (visible !== c.visible) changed = true;
      c.visible = visible;
      const d = Math.max(0, Math.hypot(cp.x - c.cx, cp.y - (c.minH + c.maxH) / 2, cp.z - c.cz) - c.radius * 0.5);
      const lod = d < 460 ? 0 : d < 2600 ? 1 : 2;
      for (let l = 0; l < 3; l++) {
        const m = c.meshes[l];
        if (m) m.visible = visible && l === lod;
      }
      if (visible) {
        const m = this.mesh(c, lod);
        m.visible = true;
        c.lod = lod;
        tris += m.geometry.index ? m.geometry.index.count / 3 : 0;
        drawn++;
      }
    }
    this.trianglesDrawn = tris;
    this.chunksDrawn = drawn;
    return changed;
  }

  /**
   * Occlusion par le relief : une tuile est cachée si les rayons de la caméra
   * vers ses quatre coins hauts et son centre sont tous coupés par le terrain.
   */
  updateOcclusion(camera: THREE.Camera): void {
    const p = camera.position;
    const size = CHUNK * this.hf.cell;
    for (const c of this.chunks) {
      const top = c.maxH + 12;
      const x0 = c.cx - size / 2;
      const z0 = c.cz - size / 2;
      const pts: [number, number][] = [
        [c.cx, c.cz],
        [x0, z0],
        [x0 + size, z0],
        [x0, z0 + size],
        [x0 + size, z0 + size],
      ];
      let hidden = true;
      for (const [x, z] of pts) {
        if (!this.hf.occludes(p.x, p.y, p.z, x, top, z, 28)) {
          hidden = false;
          break;
        }
      }
      c.occluded = hidden;
    }
  }

  /** Nombre de tuiles masquées par le relief à la dernière évaluation. */
  get occludedCount(): number {
    let n = 0;
    for (const c of this.chunks) if (c.occluded) n++;
    return n;
  }

  /** Tuile contenant (x, z). */
  chunkAt(x: number, z: number): TerrainChunk | undefined {
    const size = CHUNK * this.hf.cell;
    const i = Math.floor((x + this.hf.half) / size);
    const j = Math.floor((z + this.hf.half) / size);
    if (i < 0 || j < 0 || i >= this.side || j >= this.side) return undefined;
    return this.chunks[j * this.side + i];
  }
}
